import { useEffect, useRef, useState } from "react";
import axios from "../axios";
import { convertToCEFR, geFolderById } from "../utils";
import AnswerReport from "./tests/AnswerReport";
import "./diploma.css";

import AnimatedBorder from "./AnimatedBorder";
import RosseteHypotrochoid from "./diplom/RosseteHypotrochoid";
import useVisibility from "./hooks/useVisibility";

const Diplom = ({ hash, isVerified }) => {
  const [userResult, setUserResult] = useState({
    userName: "",
    userDate: "",
    userLevel: {
      total: 0,
      reading: 0,
      listening: 0,
      writing: 0,
      speaking: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  const svgRef = useRef(null);
  const [isSvgSignVisible, svgSignRef] = useVisibility(100);
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    if (isSvgSignVisible && !hasAnimated && svgRef.current) {
      setHasAnimated(true); // Mark animation as run
      const pathsToAnimate = svgRef.current.querySelectorAll(".st0");
      let totalDelay = 0;
      const animationDuration = 0.3; // Duration for stroke animation

      pathsToAnimate.forEach((path) => {
        const length = path.getTotalLength();
        // Set initial stroke properties for drawing effect
        path.style.strokeDasharray = length;
        path.style.strokeDashoffset = length;
        // Start with no fill
        path.style.fillOpacity = 0;
        // Apply stroke animation
        path.style.animation = `draw ${animationDuration}s linear forwards ${totalDelay}s`;
        // Schedule fill animation immediately after stroke animation
        setTimeout(() => {
          path.style.transition = `fill-opacity 0.2s linear`;
          path.style.fillOpacity = 1;
        }, totalDelay * 1000); // Fill starts as soon as this path's stroke finishes
        totalDelay += animationDuration; // Stagger next path's stroke animation
      });
    }
  }, [isSvgSignVisible, hasAnimated]);

  useEffect(() => {
    const fetchUserResult = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/diplom/test-result/${hash}`);
        // console.log("response.data====>", response.data);
        setUserResult(response.data);
      } catch (error) {
        console.error("Error fetching user result:", error);
      } finally {
        setLoading(false);
      }
    };

    if (hash) {
      fetchUserResult();
    }
  }, [hash]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid"
          width="200"
          height="200"
        >
          <title>Loading</title>
          <g>
            <circle
              stroke-linecap="round"
              fill="none"
              stroke-dasharray="50.26548245743669 50.26548245743669"
              stroke="#d5d5d5"
              stroke-width="8"
              r="32"
              cy="50"
              cx="50"
            >
              <animateTransform
                values="0 50 50;360 50 50"
                keyTimes="0;1"
                dur="1s"
                repeatCount="indefinite"
                type="rotate"
                attributeName="transform"
              ></animateTransform>
            </circle>
            <g></g>
          </g>
        </svg>
      </div>
    );
  }

  // Convert userLevel values to CEFR scores
  // console.log("@@@@@@@@@@@@", userResult.userLevel);
  const userLevelsCEFR = {
    total: convertToCEFR(userResult.userLevel.total),
    reading: convertToCEFR(userResult.userLevel.reading),
    listening: convertToCEFR(userResult.userLevel.listening),
    writing: convertToCEFR(userResult.userLevel.writing),
    speaking: convertToCEFR(userResult.userLevel.speaking),
  };

  const calculateStrokeDasharray = (level) => {
    const circumference = 257.611; // 2πr = 2 * π * 41
    const levelValues = {
      A0: 0, // 0%
      A1: (circumference * 1) / 7, // 1/7
      A2: (circumference * 2) / 7, // 2/7
      B1: (circumference * 3) / 7, // 3/7
      B2: (circumference * 4) / 7, // 4/7
      C1: (circumference * 6) / 7, // 6/7
      C2: circumference, // 7/7
    };
    return `${levelValues[level] || 0} ${circumference}`;
  };

  const levelsDescription = {
    A0: {
      text: "Novice",
      color: "yellow",
      reading: {
        description:
          "You are beginning your language learning journey. While you may not understand full sentences yet, you can recognize individual words, especially on signs or menus.",
        points: [
          "Familiarizing yourself with the alphabet and common words.",
          "Reading short texts like postcards and ads.",
          "Recognizing common text formats, such as forms, emails, and social media messages.",
        ],
      },
      listening: {
        description:
          "You are just beginning your language learning journey. While conversations and public announcements may be unclear, you can understand short, direct questions with the help of body language.",
        points: [
          "Recognizing common questions about identity and basic needs.",
          "Understanding familiar public announcements.",
          "Interpreting simple requests in daily life and classroom settings.",
        ],
      },
      writing: {
        description:
          "You are beginning to express yourself in writing. While full sentences may still be challenging, you can write individual words and simple phrases, such as personal details or familiar objects.",
        points: [
          "Practicing the alphabet and basic spelling.",
          "Writing short phrases or lists, like names, dates, and common items.",
          "Attempting simple sentences with support, such as sentence starters or templates.",
        ],
      },
      speaking: {
        description:
          "You can speak simple sentences and questions, but may struggle with more complex conversations or technical language.",
        points: [
          "Learning to introduce yourself and say common phrases.",
          "Repeating key words and short expressions from everyday life.",
          "Using tone and body language to support basic communication.",
        ],
      },
    },
    A1: {
      text: "Beginner",
      color: "orange",
      reading: {
        description:
          "You are making progress in building your vocabulary and understanding words, sentences, and even short paragraphs on everyday topics.",
        points: [
          "Can recognize and use common expressions and basic phrases to meet simple needs.",
          "Can understand familiar names, words, and very simple sentences, such as those on signs, posters, or in catalogs.",
          "Can grasp the main idea of simple informational texts and descriptions, especially with visual support.",
        ],
      },
      listening: {
        description:
          "You are developing your vocabulary and improving your understanding of sentences.",
        points: [
          "Can engage in simple interactions if the other person speaks slowly and provides support.",
          "Can recognize familiar words and basic phrases about yourself, family, and immediate surroundings when spoken clearly.",
          "Can understand carefully worded instructions and follow short, simple directions.",
        ],
      },
      writing: {
        description:
          "You are gaining confidence in writing simple sentences and familiar expressions. While your writing is still basic, you can communicate key information about yourself and everyday topics.",
        points: [
          "Can write short, simple sentences about personal details, daily routines, and familiar objects.",
          "Can fill out basic forms with personal information, such as name, address, and nationality.",
          "Can write simple notes, messages, or postcards with common phrases.",
        ],
      },
      speaking: {
        description:
          "You can communicate in basic, everyday situations with support. While conversations are short and simple, you can express familiar ideas and respond to clear, slow speech.",
        points: [
          "Can introduce yourself and talk briefly about familiar topics like family, home, and daily activities.",
          "Can ask and answer simple questions in predictable conversations.",
          "Can use basic phrases to meet immediate needs, such as ordering food or asking for directions.",
        ],
      },
    },
    A2: {
      text: "Elementary",
      color: "orange-dark",
      reading: {
        description:
          "You can understand simple texts on familiar topics and follow the main points in everyday materials.",
        points: [
          "Can read short articles, emails, and messages about common subjects like work, family, and hobbies.",
          "Can understand simple written instructions, menus, and timetables.",
          "Can grasp the main idea of straightforward texts, especially with some visual support.",
        ],
      },
      listening: {
        description:
          "You can follow slow, clear speech in routine conversations and familiar contexts.",
        points: [
          "Can understand simple questions and instructions when spoken clearly.",
          "Can recognize key details in short, predictable conversations.",
          "Can follow the general idea of everyday spoken announcements or media.",
        ],
      },
      writing: {
        description:
          "You can write short, structured texts on familiar topics, expressing simple ideas.",
        points: [
          "Can write basic personal letters, emails, or messages.",
          "Can describe everyday activities, experiences, and preferences in short sentences.",
          "Can complete simple forms and documents with necessary details.",
        ],
      },
      speaking: {
        description:
          "You can participate in simple conversations and express basic thoughts in familiar situations.",
        points: [
          "Can talk about personal interests, daily routines, and past experiences in simple terms.",
          "Can ask and answer straightforward questions in social and work-related contexts.",
          "Can use simple linking words (e.g., and, but, because) to connect ideas.",
        ],
      },
    },
    B1: {
      text: "Intermediate",
      color: "green",
      reading: {
        description:
          "You can understand texts on everyday topics and identify key points in more detailed materials.",
        points: [
          "Can read and understand emails, articles, and personal stories about familiar subjects.",
          "Can follow instructions and informational texts, such as travel guides or workplace documents.",
          "Can grasp the main ideas and opinions in simple news articles and online discussions.",
        ],
      },
      listening: {
        description:
          "You can understand clear, standard speech on familiar topics and follow everyday conversations.",
        points: [
          "Can follow the main points of discussions about work, school, or leisure activities.",
          "Can understand basic opinions and arguments in spoken conversations.",
          "Can grasp the general meaning of TV shows, podcasts, or news reports when spoken clearly.",
        ],
      },
      writing: {
        description:
          "You can write structured texts with clear ideas and opinions on familiar topics.",
        points: [
          "Can write emails, reports, and personal messages with some detail.",
          "Can describe experiences, express opinions, and explain reasons in connected sentences.",
          "Can write clear, simple arguments using common linking words (e.g., however, therefore, in addition).",
        ],
      },
      speaking: {
        description:
          "You can handle everyday conversations, express opinions, and describe experiences in some detail.",
        points: [
          "Can discuss familiar topics, such as work, school, and personal interests.",
          "Can express opinions, give explanations, and respond to others in conversations.",
          "Can manage basic travel or work-related situations, asking and answering questions naturally.",
        ],
      },
    },
    B2: {
      text: "Upper Intermediate",
      color: "cyan",
      reading: {
        description:
          "You can understand a wide range of texts, including more complex articles and opinions.",
        points: [
          "Can read and analyze detailed reports, news articles, and essays on various topics.",
          "Can follow arguments and identify key points in opinion pieces and discussions.",
          "Can understand fiction and non-fiction with some unfamiliar vocabulary, using context to infer meaning.",
        ],
      },
      listening: {
        description:
          "You can follow extended speech and discussions, even on more abstract or technical topics.",
        points: [
          "Can understand lectures, presentations, and debates on familiar subjects.",
          "Can follow conversations between native speakers, even with different accents or faster speech.",
          "Can grasp the main ideas and details in podcasts, interviews, and news reports without much repetition.",
        ],
      },
      writing: {
        description:
          "You can write clear, detailed texts on a variety of subjects, presenting arguments and opinions effectively.",
        points: [
          "Can write structured essays, reports, and reviews with supporting evidence.",
          "Can express and justify opinions, contrasting different perspectives.",
          "Can write emails, formal letters, and social media posts with appropriate tone and clarity.",
        ],
      },
      speaking: {
        description:
          "You can communicate fluently and spontaneously in most situations, discussing both concrete and abstract topics.",
        points: [
          "Can express opinions, provide explanations, and argue points with confidence.",
          "Can participate in extended discussions, responding naturally and adapting to different conversational styles.",
          "Can describe experiences, ideas, and plans in a clear, detailed, and structured way.",
        ],
      },
    },
    C1: {
      text: "Advanced",
      color: "violet",
      reading: {
        description:
          "You can understand complex and detailed texts, including abstract, technical, and literary works.",
        points: [
          "Can read and analyze in-depth articles, reports, and academic texts with ease.",
          "Can follow nuanced arguments and detect implicit meanings in editorials and essays.",
          "Can understand contemporary literature and complex narratives, recognizing tone and style.",
        ],
      },
      listening: {
        description:
          "You can follow extended speech, even when delivered quickly or with idiomatic expressions.",
        points: [
          "Can understand complex discussions, debates, and presentations on a wide range of topics.",
          "Can follow films, podcasts, and news broadcasts with minimal difficulty, including implied meanings.",
          "Can comprehend speakers with different accents and adapt to variations in speed and style.",
        ],
      },
      writing: {
        description:
          "You can produce clear, well-structured, and sophisticated texts on complex subjects.",
        points: [
          "Can write essays, reports, and research papers, developing arguments logically.",
          "Can use a wide range of vocabulary and structures to express subtle ideas and perspectives.",
          "Can adapt writing style to different contexts, including formal, academic, and professional communication.",
        ],
      },
      speaking: {
        description:
          "You can express yourself fluently and effectively, even in complex discussions.",
        points: [
          "Can participate in debates and discussions, presenting arguments persuasively.",
          "Can use idioms, expressions, and varied sentence structures naturally in conversation.",
          "Can explain and clarify ideas in a precise and structured manner, adapting to different audiences.",
        ],
      },
    },
    C2: {
      text: "Proficient",
      color: "purple",
      reading: {
        description:
          "You can effortlessly understand and analyze virtually any written text, including highly complex or specialized materials.",
        points: [
          "Can read and critically evaluate academic papers, technical reports, and literary works.",
          "Can grasp implicit meanings, subtleties, and rhetorical devices in sophisticated writing.",
          "Can understand abstract, philosophical, or theoretical texts with ease.",
        ],
      },
      listening: {
        description:
          "You can comprehend spoken language at natural speed, even in complex or unfamiliar contexts.",
        points: [
          "Can follow fast, idiomatic speech, including films, debates, and expert discussions.",
          "Can understand nuanced humor, cultural references, and implicit messages in conversations.",
          "Can process and interpret multiple viewpoints in fast-paced discussions and lectures.",
        ],
      },
      writing: {
        description:
          "You can produce well-structured, precise, and sophisticated texts in any context.",
        points: [
          "Can write persuasive essays, reports, and analyses with clarity and depth.",
          "Can adjust tone, style, and structure for academic, professional, or creative writing.",
          "Can express complex ideas with accuracy, fluency, and stylistic finesse.",
        ],
      },
      speaking: {
        description:
          "You can express yourself effortlessly, with complete fluency and natural accuracy.",
        points: [
          "Can engage in high-level debates, using nuanced arguments and rhetorical techniques.",
          "Can adapt language to any context, from formal speeches to casual conversations.",
          "Can convey complex emotions, abstract concepts, and subtle distinctions in meaning.",
        ],
      },
    },
  };
  const levelDescription = levelsDescription[userLevelsCEFR.total].text;
  const levelColor = levelsDescription[userLevelsCEFR.total].color;

  if (isVerified) {
    return (
      <div className="verification">
        <style jsx>{`
          .verification {
            display: flex;
            align-items: center;
            height: 100vh;
            flex-direction: column;
            justify-content: center;
            font-size: 16px;
            padding: 20px;
          }
          .verification .title {
            text-align: center;
            line-height: 18px;
            padding-bottom: 15px;
          }
          .verLinks a {
            display: block;
            text-align: center;
            border: 1px solid #000;
            border-radius: 5px;
            padding: 3px 11px;
            margin: 0 0 8px;
          }
        `}</style>
        <div className="title">
          Verified Test Recordings
          <br /> for {userResult.userName}
        </div>

        <div className="verLinks">
          {userResult.speakingId && (
            <a
              href={`/replay/speaking/${userResult.speakingUuid}/${userResult.speakingMimeType}/${userResult.speakingId}`}
            >
              Speaking
            </a>
          )}

          {userResult.writingId && (
            <a
              href={`/replay/writing/${userResult.writingUuid}/${userResult.writingMimeType}/${userResult.writingId}`}
            >
              Writing
            </a>
          )}
          {userResult.readingId && (
            <a
              href={`/replay/reading/${userResult.readingUuid}/${userResult.readingMimeType}/${userResult.readingId}`}
            >
              Reading
            </a>
          )}
          {userResult.listeningId && (
            <a
              href={`/replay/listening/${userResult.listeningUuid}/${userResult.listeningMimeType}/${userResult.listeningId}`}
            >
              Listening
            </a>
          )}
        </div>
      </div>
    );
  }

  const formattedDate = new Date(userResult.userDate)
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(",", "");

  // console.log("############ =====>", userLevelsCEFR);

  let avatar = "/static/default-avatar.jpg";
  if (userResult.testSpeakingId && userResult.speakingUuid) {
    avatar = `/diplom-answers/${geFolderById(userResult.testSpeakingId)}/${
      userResult.speakingUuid
    }.jpg`;
  } else if (userResult.testWritingId && userResult.writingUuid) {
    avatar = `/diplom-answers/${geFolderById(userResult.testWritingId)}/${
      userResult.writingUuid
    }.jpg`;
  } else if (userResult.listeningId && userResult.listeningUuid) {
    avatar = `/diplom-answers/${geFolderById(userResult.listeningId)}/${
      userResult.listeningUuid
    }.jpg`;
  } else if (userResult.testReadingId && userResult.readingUuid) {
    avatar = `/diplom-answers/${geFolderById(userResult.testReadingId)}/${
      userResult.readingUuid
    }.jpg`;
  }

  return (
    <div>
      <style jsx>{`
        /* --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgba(59, 130, 246, 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000; */
      `}</style>
      <main className="flex flex-col items-center mb-12 text-center lg:mt-12">
        <div className="flex flex-col items-center shadow-paper page_container__7giYo">
          <div class="bg-background2  text-black w-full h-[250px] text-center mb-[57px]">
            <div class="text-4xl font-bold pt-7 ">
              <a href="https://diplom.org" className="cursor-pointer">
                Diplom.org
              </a>
            </div>
            <div class="bg-white text-black inline-block px-2.5 py-2 rounded-md my-[20px]">
              English Proficiency Diploma
            </div>
            <img
              src={avatar}
              alt="Profile" // grayscale
              class="gradient-border w-[200px] h-[200px] object-cover rounded-lg border-[5px] border-white mx-auto"
            />
          </div>

          <div className="px-4 pt-12 lg:px-12">
            <p className="mb-8 text-3xl font-bold lg:text-name text-text-dark page_name__3uR8A">
              {userResult.userName}
            </p>
            <p className="text-text-dark font-medium mb-10 text-base max-w-[370px] mx-auto">
              has successfully earned the Diploma in English Proficiency and
              achieved the following level:
            </p>

            <div class="relative w-[150px] h-[150px] bg-black rounded-[10px] mx-auto mt-2.5 flex flex-col justify-center">
              <div class="text-[66px] leading-[64px] text-white">
                {userLevelsCEFR.total}
              </div>
              <div class="text-[20px] leading-[22px] pt-[10px] text-white">
                {levelDescription}
              </div>
            </div>
          </div>
          <div className="text-[10px] mt-[69px] mb-[3px]">
            Click for Verification
          </div>

          <div className="p-5 flex flex-col sm:flex-row h-auto border-2 border-[#dbdee1] rounded-2xl gap-5">
            {userResult.speakingId && (
              <AnimatedBorder isSpeaking>
                <div
                  onClick={() => {
                    window.location.href = `/replay/speaking/${userResult.speakingUuid}/${userResult.speakingMimeType}/${userResult.speakingId}/${userResult.speakingBundleId}`;
                  }}
                  className="group h-[87px] w-[87px] cursor-pointer rounded border border-[#eee] bg-[#eee] hover:bg-black hover:text-white"
                >
                  <div className="text-white text-[14px] uppercase bg-[#747272] group-hover:bg-black leading-4 px-[5px] py-1 font-bold rounded-t">
                    Speaking
                  </div>
                  <div className="h-12 text-[40px] text-[#4b4b4b] group-hover:text-white flex items-center justify-center">
                    {userLevelsCEFR.speaking}
                  </div>
                </div>
              </AnimatedBorder>
            )}

            {userResult.writingId && (
              <div
                onClick={() => {
                  window.location.href = `/replay/writing/${
                    userResult.writingUuid
                  }/${userResult.writingMimeType}/${userResult.writingId}/${
                    userResult.writing1Url
                  }${
                    userResult.writing2Url ? `/${userResult.writing2Url}` : ""
                  }`;
                }}
                className="group h-[87px] w-[87px] cursor-pointer rounded border border-[#eee] bg-[#eee] hover:bg-black hover:text-white"
              >
                <div className="text-white text-[14px] uppercase bg-[#747272] group-hover:bg-black leading-4 px-[5px] py-1 font-bold rounded-t">
                  Writing
                </div>
                <div className="h-12 text-[40px] text-[#4b4b4b] group-hover:text-white flex items-center justify-center">
                  {userLevelsCEFR.writing}
                </div>
              </div>
            )}

            {userResult.testReadingId && (
              <div
                onClick={() => {
                  window.location.href = `/replay/reading/${userResult.readingUuid}/${userResult.readingMimeType}/${userResult.readingId}/${userResult.readingId}`;
                }}
                className="group h-[87px] w-[87px] cursor-pointer rounded border border-[#eee] bg-[#eee] hover:bg-black hover:text-white"
              >
                <div className="text-white text-[14px] uppercase bg-[#747272] group-hover:bg-black leading-4 px-[5px] py-1 font-bold rounded-t">
                  Reading
                </div>
                <div className="h-12 text-[40px] text-[#4b4b4b] group-hover:text-white flex items-center justify-center">
                  {userLevelsCEFR.reading}
                </div>
              </div>
            )}

            {userResult.testListeningId && (
              <div
                onClick={() => {
                  window.location.href = `/replay/listening/${userResult.listeningUuid}/${userResult.listeningMimeType}/${userResult.listeningId}/${userResult.listeningId}`;
                }}
                className="group h-[87px] w-[87px] cursor-pointer rounded border border-[#eee] bg-[#eee] hover:bg-black hover:text-white"
              >
                <div className="text-white text-[14px] uppercase bg-[#747272] group-hover:bg-black leading-4 px-[5px] py-1 font-bold rounded-t">
                  Listening
                </div>
                <div className="h-12 text-[40px] text-[#4b4b4b] group-hover:text-white flex items-center justify-center">
                  {userLevelsCEFR.listening}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center justify-between w-full px-10 pt-24 pb-16 sm:flex-row sm:px-16">
            <div id="circle" className="relative w-[120px] h-[120px]">
              <div className="circleWrapper">
                <div className="circleInner">
                  <img src="/logo192.png" alt="logo" />
                </div>
              </div>
              <style>{`
                .circleWrapper {
                  width: 120px;
                  height: 120px;
                  border-radius: 100px;
                  background-color: #f1f3f4;
                  display: flex;
                  border: 1px solid #bdc1c7;
                  align-items: center;
                  justify-content: center;
                }

                .circleInner {
                  width: 80px;
                  height: 80px;
                  border-radius: 100px;
                  background-color: #fff;
                  display: flex;
                  border: 6px solid #bdc1c7;
                  align-items: center;
                  justify-content: center;
                }
                .circleWrapper img {
                  width: 47px;
                  margin-right: -5px;
                  border-radius: 12px;
                }
                #circle svg {
                  position: absolute;
                  left: 0;
                  top: 0;
                  animation: rotate 17s infinite linear;
                  scale: 1.9;
                }

                #circle text {
                  font-family: "Helvetica Neue", Arial;
                  font-size: 15px;
                  font-weight: bold;
                }

                @keyframes rotate {
                  from {
                    transform: rotate(360deg);
                  }
                  to {
                    transform: rotate(0deg);
                  }
                }
              `}</style>
              <svg
                version="1.1"
                x="0px"
                y="0px"
                viewBox="0 0 300 300"
                enableBackground="new 0 0 300 300"
              >
                <defs>
                  <path
                    id="circlePath"
                    d=" M 150, 150 m -60, 0 a 60,60 0 0,1 120,0 a 60,60 0 0,1 -120,0 "
                  />
                </defs>
                <circle cx="150" cy="150" r="60" fill="none" />
                <g>
                  <use xlinkHref="#circlePath" fill="none" />
                  <text fill="#5f6369">
                    <textPath
                      xlinkHref="#circlePath"
                      startOffset="25%"
                      textAnchor="middle"
                    >
                      Awarded on {formattedDate}
                    </textPath>
                    <textPath
                      xlinkHref="#circlePath"
                      startOffset="75%"
                      textAnchor="middle"
                    >
                      Awarded on {formattedDate}
                    </textPath>
                  </text>
                </g>
              </svg>
            </div>

            <div className="w-[110px] text-[10px] -mt-3 sm:opacity-100 opacity-0">
              Scan for Verification
              <img src={userResult.qrUrl} alt="QR Code" />
            </div>

            <div className="max-w-[150px] text-[10px] sm:-ml-8 ml-0">
              <svg
                viewBox="0 0 73.42 56.43"
                width="150"
                style={{ marginBottom: "-40px", marginTop: "13px" }}
                ref={svgRef}
              >
                <defs>
                  <style type="text/css">{`
          @keyframes draw {
            to {
              stroke-dashoffset: 0;
            }
          }
          .st0 {
            fill: #231f20; /* Fill color for solid signature */
            stroke: #231f20; /* Stroke color for pen-like effect */
            stroke-width: 0.5; /* Thinner stroke for delicate look */
            stroke-linecap: round; /* Rounded ends for natural look */
            stroke-linejoin: round; /* Smooth joins for curves */
          }
        `}</style>
                </defs>
                <g id="Layer_2" ref={svgSignRef}>
                  <path
                    className="st0"
                    d="M17.87,52.77a208.33,208.33,0,0,0,19.86-23c3.05-4.11,6-8.32,8.67-12.67C48.61,13.5,50.56,9,48.26,5a6,6,0,0,0-4.77-3.06,11.08,11.08,0,0,0-6.38,1.9A39.52,39.52,0,0,0,31.63,8a57.23,57.23,0,0,0-5,4.88A72.3,72.3,0,0,0,18.28,24.2a53.34,53.34,0,0,0-6.13,13.49c-.41,1.53-1.24,4.4.79,5.19,1.16.45,2.37-.28,3.37-.81,1.17-.61,2.32-1.27,3.44-2,4.62-2.84,9.07-6,13.58-9l3.52-2.36L36,27.93A45.73,45.73,0,0,0,33,36a.6.6,0,0,0,.87.67c3-1.37,6.06-2.78,9.1-4.15l-.73-.94a2.58,2.58,0,0,0,.41,3.72c1.41,1.13,3.38.5,4.57-.63a.64.64,0,0,0-.13-1,1.27,1.27,0,1,0-.27,2.19c.77-.31.43-1.59-.35-1.26-.2.08-.16,0,0,.16l-.13-1a2.49,2.49,0,0,1-2.8.62,1.37,1.37,0,0,1-.43-2,.6.6,0,0,0-.73-.94c-3,1.4-6.06,2.76-9.09,4.16l.87.67a41.12,41.12,0,0,1,3-7.81c.26-.5-.28-1.18-.82-.82-4.34,2.88-8.65,5.8-13,8.69-2,1.36-4.08,2.71-6.22,3.91l-1.53.83a3.64,3.64,0,0,1-1.91.67c-.93-.13-.82-1.42-.71-2.1.8-4.65,3-9.07,5.33-13.14a74.3,74.3,0,0,1,7.44-10.92A58.56,58.56,0,0,1,35.1,6.48c3-2.26,8.09-5.54,11.45-2.11C49.71,7.6,48,12.49,46,15.82c-2.41,4.1-5.16,8-7.94,11.89a206.79,206.79,0,0,1-18.31,22c-.81.84-1.63,1.68-2.45,2.51a.41.41,0,0,0,.58.58Z"
                    transform="translate(-11.63 -1.87)"
                  />
                  <path
                    className="st0"
                    d="M62.64,9.44c-8.07,12.62-16,25.31-24,38a72.8,72.8,0,0,1-6.83,9.71l1.06.82A92.47,92.47,0,0,1,50.22,34q3-3,6.27-5.71c.51-.42-.18-1.34-.72-.92a20.57,20.57,0,0,0-6,7.36.63.63,0,0,0,.7.91A8.53,8.53,0,0,0,54.67,33l-1-.13c.22.47.43,1,.66,1.42a.64.64,0,0,0,.86.23c.48-.37,1-.74,1.44-1.09a.61.61,0,0,0-.14-1.11c-1.13-.34-1.55,1.11-1,1.89a1.25,1.25,0,0,0,1.52.39,2.74,2.74,0,0,0,1.34-1.48l-1,.13a2.12,2.12,0,0,0,3.4-.8l-.72.41a30.24,30.24,0,0,0,11.53-.36c.82-.19.46-1.39-.34-1.25A11.77,11.77,0,0,0,63.76,36L65,36.5a45.47,45.47,0,0,0,2.37-8.56.64.64,0,0,0-1.06-.62A1.43,1.43,0,0,0,66,29a2,2,0,0,0,1.49,1,21.88,21.88,0,0,0,2.69.27c.94.07,1.88.13,2.82.17a103.23,103.23,0,0,0,11.4-.07c.86-.05.86-1.39,0-1.33q-4.59.29-9.2.18c-1.51,0-3-.1-4.53-.2-.73-.05-1.47-.1-2.2-.17-.22,0-1.8-.22-1.25-.68l-1.07-.62a43.41,43.41,0,0,1-2.47,8.53c-.31.78.84,1.11,1.26.53a10.44,10.44,0,0,1,6.6-4.2l-.34-1.25a27.89,27.89,0,0,1-10.88.48.61.61,0,0,0-.72.41.88.88,0,0,1-1.38.21.64.64,0,0,0-1,.13,2.15,2.15,0,0,1-.74.94c-.16.1,0,0,0,0a.2.2,0,0,1,0-.13c.06-.14,0-.08-.29.16L56,32.34c-.48.36-1,.71-1.46,1.06l.85.22c-.21-.48-.43-.95-.65-1.43a.64.64,0,0,0-1-.13,7.41,7.41,0,0,1-3.65,2.38l.71.92a19.2,19.2,0,0,1,5.66-7.07l-.84-.83A93.53,93.53,0,0,0,35.87,49.9q-2.26,3.59-4.2,7.37c-.34.65.52,1.42,1.06.81a61.53,61.53,0,0,0,6-8.57q3-4.77,5.93-9.54,6-9.64,12-19.3Q60,15.25,63.3,9.83c.26-.42-.39-.8-.66-.39Z"
                    transform="translate(-11.63 -1.87)"
                  />
                  <path
                    className="st0"
                    d="M28.46,25.06q20-1.8,40.11-2.26,5.7-.14,11.41-.17a.38.38,0,0,0,0-.75q-20.1.09-40.15,1.58c-3.79.27-7.58.59-11.37.93-.42,0-.42.71,0,.67Z"
                    transform="translate(-11.63 -1.87)"
                  />
                </g>
              </svg>
              <div className="border-t border-black">Executive Director</div>
            </div>
          </div>
          <style>{`
          
          
  .CircularProgress_placeholder__EUxMP {
    stroke-dashoffset: 0;
    stroke: #eee;
  }
  .CircularProgress_circle__QiVyT {
    stroke-linecap: round;
    position: absolute;
    fill: none;
  }

  @keyframes progress {
    from {
      stroke-dasharray: 0 257.611;
    }
    to {
      stroke-dasharray: var(--progress-value) 257.611;
    }
  }

  .CircularProgress_circle__QiVyT {
    stroke-dasharray: 0 257.611;
    opacity: 0;
    animation: progress 1s ease-out forwards,
      fadeIn 0.5s ease-out forwards;
    animation-timeline: view();
    animation-range: entry 25% cover 50%;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .CircularProgress_circle__QiVyT {
      animation: none;
      stroke-dasharray: var(--progress-value) 257.611;
      opacity: 1;
    }
  }
          `}</style>

          <div className="relative w-full max-w-full pt-10 bg-background2 pb-14 lg:pb-16">
            <h2 className="text-xl font-bold">Result on the Spectrum</h2>
            <div className="pt-5.5 lg:pt-7">
              <div className="max-w-full overflow-auto">
                <div className="hidden px-4 lg:block">
                  <table className="border-separate border-spacing-2 m-center">
                    <tbody>
                      <tr>
                        <td className="p-3 text-base font-bold text-left whitespace-nowrap">
                          CEFR
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A0"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A0</div>
                            <p className="text-xs">Novice</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A1</div>
                            <p className="text-xs">Beginner</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "A2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">A2</div>
                            <p className="text-xs">Elementary</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "B1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">B1</div>
                            <p className="text-xs">Intermediate</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "B2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">B2</div>
                            <p className="text-xs">Upper Intermediate</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "C1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">C1</div>
                            <p className="text-xs">Advanced</p>
                          </div>
                        </td>
                        <td
                          className={`p-3 align-top max-w-[130px] ${
                            userLevelsCEFR.total === "C2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "12.5%" }}
                        >
                          <div className="text-base text-left">
                            <div className="font-bold">C2</div>
                            <p className="text-xs">Proficient</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="visible lg:hidden px-3.5">
                  <table className="w-full border-separate border-spacing-2">
                    <thead>
                      <tr>
                        <td className="px-2 text-base font-bold text-left">
                          CEFR
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A0"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A0</p>
                            <p className="mt-1 text-xs">Novice</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A1</p>
                            <p className="mt-1 text-xs">Beginner</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "A2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">A2</p>
                            <p className="mt-1 text-xs">Elementary</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "B1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">B1</p>
                            <p className="mt-1 text-xs">Intermediate</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "B2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">B2</p>
                            <p className="mt-1 text-xs">Upper Intermediate</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "C1"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">C1</p>
                            <p className="mt-1 text-xs">Advanced</p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className={`p-2.5 align-top ${
                            userLevelsCEFR.total === "C2"
                              ? `bg-chart-${levelColor} text-white`
                              : "ScoreComparisonTable_unselectedItem___E4kM"
                          }`}
                          style={{ width: "100%" }}
                        >
                          <div className="text-base text-left">
                            <p className="font-bold">C2</p>
                            <p className="mt-1 text-xs">Proficient</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="max-w-2xl px-6 mx-auto mt-8">
              <p className="text-sm">
                The achieved level is{" "}
                <strong>
                  {userLevelsCEFR.total}{" "}
                  {levelsDescription[userLevelsCEFR.total].text}
                </strong>{" "}
                according to the Common European Framework of Reference (CEFR).
                This score is calculated as an average of the skill section
                scores.
              </p>
              <div className="flex flex-col m-auto text-left gap-y-6 lg:gap-y-5 mt-11 lg:mt-8">
                {userResult.testSpeakingId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col mb-4 md:items-center">
                      <div className="flex flex-col mb-4 md:flex-row md:mb-4">
                        <div className="flex flex-row items-center justify-center">
                          <div
                            className="relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <div
                              className="absolute flex items-center justify-center"
                              style={{ width: "90px", height: "90px" }}
                            >
                              <span
                                className={`text-6xl text-chart-${
                                  levelsDescription[userLevelsCEFR.speaking]
                                    .color
                                } !text-2xl font-bold`}
                              >
                                {userLevelsCEFR.speaking}
                              </span>
                            </div>
                            <svg style={{ width: "90px", height: "90px" }}>
                              <circle
                                className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  transform: "translate(4px, 4px)",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  strokeDasharray: "257.610597594363",
                                }}
                              />
                              <circle
                                className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                  levelsDescription[userLevelsCEFR.speaking]
                                    .color
                                }`}
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  strokeDashoffset: "0",
                                  translate: "4px -4px",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  "--progress-value":
                                    calculateStrokeDasharray(
                                      userLevelsCEFR.speaking,
                                    ).split(" ")[0] + "px",
                                }}
                              />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div>
                              <p className="font-light">English</p>
                              <h3 className="font-bold text-xl lg:text-1.5xl">
                                Speaking
                              </h3>
                              <p>
                                <span className="font-light">
                                  {
                                    levelsDescription[userLevelsCEFR.speaking]
                                      .text
                                  }
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <PartAnswerDetails
                          details={[
                            {
                              value: userResult.userLevel.speaking,
                              label: "Pronunciation",
                            },
                            {
                              value: Math.min(
                                userResult.userLevel.speaking + 0.5,
                                9,
                              ),

                              label: "Lexical Resource",
                            },
                            {
                              value: userResult.userLevel.speaking - 0.5,
                              label: "Fluency Coherence",
                            },
                            {
                              value: userResult.userLevel.speaking,
                              label: "Grammatical Range",
                            },
                          ]}
                          detailedReportLink={`https://app.ieltsing.com/diplom-speaking-report/${userResult.speakingBundleId}`}
                        />
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.speaking].speaking
                              .description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.speaking
                            ].speaking.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {userResult.testWritingId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col mb-4 md:items-center">
                      <div className="flex flex-col mb-4 md:flex-row md:mb-4">
                        <div className="flex flex-row items-center justify-center">
                          <div
                            className="relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <div
                              className="absolute flex items-center justify-center"
                              style={{ width: "90px", height: "90px" }}
                            >
                              <span
                                className={`text-6xl text-chart-${
                                  levelsDescription[userLevelsCEFR.writing]
                                    .color
                                } !text-2xl font-bold`}
                              >
                                {userLevelsCEFR.writing}
                              </span>
                            </div>
                            <svg style={{ width: "90px", height: "90px" }}>
                              <circle
                                className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  transform: "translate(4px, 4px)",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  strokeDasharray: "257.610597594363",
                                }}
                              ></circle>
                              <circle
                                className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                  levelsDescription[userLevelsCEFR.writing]
                                    .color
                                }`}
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  strokeDashoffset: "0",
                                  translate: "4px -4px",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  "--progress-value":
                                    calculateStrokeDasharray(
                                      userLevelsCEFR.writing,
                                    ).split(" ")[0] + "px",
                                }}
                              ></circle>
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div>
                              <p className="font-light">English</p>
                              <h3 className="font-bold text-xl lg:text-1.5xl">
                                Writing
                              </h3>
                              <p>
                                <span className="font-light">
                                  {levelsDescription[
                                    userLevelsCEFR.writing
                                  ].text.replace(
                                    "Upper Intermediate",
                                    "Intermediate+",
                                  )}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <PartAnswerDetails
                          isAnimatedBorderDisabled
                          details={[
                            {
                              value: userResult.userLevel.writing - 0.5,
                              label: "Task Achievement",
                            },
                            {
                              value: userResult.userLevel.writing,
                              label: "Lexical Resource",
                            },
                            {
                              value: userResult.userLevel.writing,
                              label: "Coherence Cohesion",
                            },
                            {
                              value: Math.min(
                                userResult.userLevel.writing + 0.5,
                                9,
                              ),
                              label: "Grammatical Range",
                            },
                          ]}
                          detailedReportLink={`https://app.ieltsing.com/diplom-writing-report/${
                            userResult.testId
                              ? userResult.writing2Url
                              : userResult.writing1Url
                          }`}
                        />
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.writing].writing
                              .description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.writing
                            ].writing.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {userResult.testReadingId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col mb-4 md:items-center">
                      <div className="flex flex-col mb-4 md:flex-row md:mb-4">
                        <div className="flex flex-row items-center justify-center">
                          <div
                            className="relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <div
                              className="absolute flex items-center justify-center"
                              style={{ width: "90px", height: "90px" }}
                            >
                              <span
                                className={`text-6xl text-chart-${
                                  levelsDescription[userLevelsCEFR.reading]
                                    .color
                                } !text-2xl font-bold`}
                              >
                                {userLevelsCEFR.reading}
                              </span>
                            </div>
                            <svg style={{ width: "90px", height: "90px" }}>
                              <circle
                                className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  transform: "translate(4px, 4px)",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  strokeDasharray: "257.610597594363",
                                }}
                              ></circle>
                              <circle
                                className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                  levelsDescription[userLevelsCEFR.reading]
                                    .color
                                }`}
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  strokeDashoffset: "0",
                                  translate: "4px -4px",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  "--progress-value":
                                    calculateStrokeDasharray(
                                      userLevelsCEFR.reading,
                                    ).split(" ")[0] + "px",
                                }}
                              ></circle>
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div>
                              <p className="font-light">English</p>
                              <h3 className="font-bold text-xl lg:text-1.5xl">
                                Reading
                              </h3>
                              <p>
                                <span className="font-light">
                                  {
                                    levelsDescription[userLevelsCEFR.reading]
                                      .text
                                  }
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <PartAnswerDetails
                          isAnimatedBorderDisabled
                          details={[
                            {
                              value: userResult.userLevel.reading,
                              label: "Text Understanding",
                            },
                            {
                              value: Math.min(
                                userResult.userLevel.reading + 0.5,
                                9,
                              ),
                              label: "Detail Recognition",
                            },
                            {
                              value: userResult.userLevel.reading - 0.5,
                              label: "Vocabulary Range",
                            },
                            {
                              value: userResult.userLevel.reading,
                              label: "Logical Reasoning",
                            },
                          ]}
                          detailedReportLink={`/test-result/reading/${userResult.readingId}`}
                        />
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.reading].reading
                              .description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.reading
                            ].reading.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {userResult.testListeningId && (
                  <section className="p-6 bg-white rounded-lg lg:p-10">
                    <div className="flex flex-col mb-4 md:items-center">
                      <div className="flex flex-col mb-4 md:flex-row md:mb-4">
                        <div className="flex flex-row items-center justify-center">
                          <div
                            className="relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <div
                              className="absolute flex items-center justify-center"
                              style={{ width: "90px", height: "90px" }}
                            >
                              <span
                                className={`text-6xl text-chart-${
                                  levelsDescription[userLevelsCEFR.listening]
                                    .color
                                } !text-2xl font-bold`}
                              >
                                {userLevelsCEFR.listening}
                              </span>
                            </div>
                            <svg style={{ width: "90px", height: "90px" }}>
                              <circle
                                className="CircularProgress_placeholder__EUxMP CircularProgress_circle__QiVyT stroke-chart-placeholder-section-zero"
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  transform: "translate(4px, 4px)",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  strokeDasharray: "257.610597594363",
                                }}
                              ></circle>
                              <circle
                                className={`origin-center -rotate-90 CircularProgress_circle__QiVyT stroke-chart-${
                                  levelsDescription[userLevelsCEFR.listening]
                                    .color
                                }`}
                                cx="41"
                                cy="41"
                                r="41"
                                style={{
                                  strokeDashoffset: "0",
                                  translate: "4px -4px",
                                  width: "90px",
                                  height: "90px",
                                  strokeWidth: "4",
                                  "--progress-value":
                                    calculateStrokeDasharray(
                                      userLevelsCEFR.listening,
                                    ).split(" ")[0] + "px",
                                }}
                              ></circle>
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div>
                              <p className="font-light">English</p>
                              <h3 className="font-bold text-xl lg:text-1.5xl">
                                Listening
                              </h3>
                              <p>
                                <span className="font-light">
                                  {
                                    levelsDescription[userLevelsCEFR.listening]
                                      .text
                                  }
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <PartAnswerDetails
                          isAnimatedBorderDisabled
                          details={[
                            {
                              value: userResult.userLevel.listening - 0.5,
                              label: "Audio Understanding",
                            },
                            {
                              value: userResult.userLevel.listening,
                              label: "Detail Recognition",
                            },
                            {
                              value: userResult.userLevel.listening,
                              label: "Vocabulary Range",
                            },
                            {
                              value: Math.min(
                                userResult.userLevel.listening + 0.5,
                                9,
                              ),
                              label: "Speaker Attitude",
                            },
                          ]}
                          detailedReportLink={`/test-result/listening/${userResult.listeningId}`}
                        />
                      </div>
                      <div className="flex flex-col items-stretch">
                        <p className="flex-grow mt-2 text-sm">
                          {
                            levelsDescription[userLevelsCEFR.listening]
                              .listening.description
                          }
                        </p>
                        <div>
                          <ul className="pl-3 mt-4 list-disc">
                            {levelsDescription[
                              userLevelsCEFR.listening
                            ].listening.points.map((point, index) => (
                              <li key={index} className="mt-2 text-sm">
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
          <footer className="flex flex-col items-center w-full px-6 pt-8 m-center lg:px-0">
            <h4 className="py-8 text-xl font-bold text-center Footer_title__7ywZX">
              About Diplom.org
            </h4>
            <div className="max-w-[672px] Footer_content__V9MfQ">
              <p>
                Diplom.org provides online language certification for students
                and professionals. Powered by{` `}
                <div
                  onClick={() => {
                    window.location.href = "https://ieltsing.com";
                  }}
                  style={{
                    textDecorationLine: "underline",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                >
                  IELTSing.com
                </div>
                , we specialize in IELTS-format assessments. With a commitment
                to quality and accessibility, we help learners worldwide achieve
                their goals. IELTSing.com has evaluated over 5 million writings
                and thousands of IELTS-speaking responses, setting the standard
                for accurate, reliable assessment.
              </p>
            </div>
            <div className="flex flex-col items-center gap-5 pb-12 md:flex-row mt-9">
              {/* <a
                className="min-w-[280px] h-12 flex items-center justify-center w-full md:w-auto rounded-full border border-text-dark hover:bg-text-dark/10"
                href="https://certificates-eu-west-1.efset.org/v2/certificates/BtrDzp/pdf?locale=en"
                target="_blank"
                rel="noreferrer"
              >
                Download PDF version
              </a> */}
              <a
                className="min-w-[280px] h-12 flex items-center justify-center w-full md:w-auto rounded-full border border-text-dark hover:bg-text-dark/10 gap-x-3"
                href={`https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=English%20Proficiency%20Diploma%20-%20${
                  userLevelsCEFR.total
                }%20${levelDescription}%20Level&organizationId=106917396&issueYear=${new Date(
                  userResult.userDate,
                ).getFullYear()}&issueMonth=${
                  new Date(userResult.userDate).getMonth() + 1
                }&certUrl=https%3A%2F%2Fdiplom.org%2F%23${hash}`}
                target="_blank"
                rel="noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 !w-4.5 !h-4.5 fill-text-primary"
                  viewBox="0 0 19 19"
                >
                  <path d="M4.88528 2.29843C4.88528 3.35533 4.1046 4.21209 2.8211 4.21209C1.61369 4.21044 0.833008 3.35533 0.833008 2.29843C0.833008 1.24153 1.6385 0.384766 2.87237 0.384766C4.10625 0.384766 4.86047 1.21507 4.88528 2.29843ZM0.933901 18.0295V5.72053H4.75957V18.0295H0.933901Z"></path>
                  <path d="M7.04864 9.64712C7.04864 8.11222 6.99902 6.82872 6.94775 5.72055H10.2706L10.4476 7.43243H10.5237C11.0265 6.62694 12.2604 5.44434 14.3245 5.44434C16.8419 5.44434 18.7291 7.1314 18.7291 10.7553V18.0295H14.9034V11.2085C14.9034 9.62231 14.3493 8.5406 12.965 8.5406C11.9081 8.5406 11.2795 9.27001 11.0017 9.97461C10.9008 10.226 10.876 10.5783 10.876 10.9306V18.0279H7.05029V9.64546L7.04864 9.64712Z"></path>
                </svg>
                Add to LinkedIn
              </a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

const PartAnswerDetail = ({ value, label }) => {
  const percentage = (value / 9) * 100;
  const barRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            barRef.current.style.width = `${percentage}%`;
            barRef.current.style.transition = "width 1s ease-out";
            observer.unobserve(barRef.current);
          }
        });
      },
      { threshold: 0.5 },
    );

    if (barRef.current) observer.observe(barRef.current);
  }, [percentage]);

  return (
    <div className="w-[120px]">
      <div className="h-[5px] bg-white rounded-[3px] mb-[-2px] relative overflow-hidden">
        <div ref={barRef} className="h-full bg-[#444] rounded-[3px] w-0"></div>
      </div>
      <b className="text-[11px] font-bold">{label}</b>
    </div>
  );
};

const PartAnswerDetails = ({
  details,
  detailedReportLink,
  isAnimatedBorderDisabled = false,
}) => {
  // Sample IELTS values (replace with dynamic data from Nest.js API)

  return (
    <div className="md:ml-[58px] self-center md:self-auto mt-12 md:mt-0">
      <AnimatedBorder isAnimatedBorderDisabled={isAnimatedBorderDisabled}>
        <div
          onClick={() => {
            window.location.href = detailedReportLink;
          }}
          className="relative cursor-pointer mx-auto bg-[#eee] grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-[13px] p-[15px] pb-[10px] w-[159px] md:w-full"
        >
          <div className="absolute top-[-18px] left-1/2 transform -translate-x-1/2 text-[10px] font-medium">
            Click for Details
          </div>

          {details.map((detail, index) => (
            <PartAnswerDetail
              key={index}
              value={detail.value}
              label={detail.label}
            />
          ))}
        </div>{" "}
      </AnimatedBorder>
    </div>
  );
};

export default Diplom;
